import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SummerSparkles } from '@/components/SummerSparkles';
import { TravelMoodPills } from '@/components/TravelMoodPills';
import { useCountry } from '@/contexts/CountryContext';
import { BRAND } from '@/lib/brand';
import { useStats } from '@/hooks/useStats';

export const Hero = () => {
  const { country } = useCountry();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { stats } = useStats(country.code);
  const liveBusinesses = stats?.listings ?? country.stats.businesses;
  const liveCities = stats?.cities ?? country.stats.cities;
  const liveRating = stats?.avg_rating ?? country.stats.rating;

  const submit = (e) => {
    e.preventDefault();
    navigate(`/search${query ? `?q=${encodeURIComponent(query)}` : ''}`);
  };

  return (
    <section className="relative bg-gradient-to-br from-[#00AA6C] to-[#008F5A] text-white py-20 overflow-hidden" data-testid="hero-section">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${country.heroImage})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#00AA6C]/80 via-[#00AA6C]/60 to-[#008F5A]/50" aria-hidden />
      <SummerSparkles count={40} color="white" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center animate-fade-in-up">
          <h1 className="font-heading text-4xl font-bold sm:text-5xl lg:text-6xl mb-6 leading-tight" data-testid="hero-title">
            {country.tagline}
          </h1>
          <p className="text-xl sm:text-2xl mb-4 text-white/90 font-medium">{country.subtitle}</p>
          <p className="text-sm sm:text-base text-white/75 max-w-3xl mx-auto mb-8 leading-relaxed">
            {BRAND.description}
          </p>

          {/* Primary CTAs */}
          <div className="flex flex-wrap gap-3 justify-center mb-6">
            <Button variant="red" size="lg" asChild>
              <Link to="/plan-trip" data-testid="hero-plan-trip"><Sparkles className="w-4 h-4" /> Plan your trip</Link>
            </Button>
            <Button variant="glass" size="lg" asChild>
              <Link to={`/${country.code}`} data-testid="hero-explore-country">Explore {country.name}</Link>
            </Button>
            <Button variant="glass" size="lg" asChild>
              <Link to="/categories" data-testid="hero-discover">Discover places</Link>
            </Button>
          </div>

          {/* Search bar */}
          <form className="mx-auto max-w-2xl" onSubmit={submit} role="search" aria-label="Site search">
            <div className="relative flex items-center">
              <label htmlFor="hero-search" className="sr-only">Search businesses</label>
              <Input
                id="hero-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search restaurants, hotels, attractions..."
                className="w-full h-14 pl-12 pr-32 text-lg rounded-full border-0 shadow-xl placeholder:text-gray-500 text-gray-900 bg-white focus-visible:ring-2 focus-visible:ring-white"
                data-testid="hero-search-input"
              />
              <Search className="absolute left-4 h-6 w-6 text-gray-400" aria-hidden />
              <Button type="submit" variant="red" size="lg" className="absolute right-2 h-10 rounded-full" data-testid="hero-search-submit">
                Search
              </Button>
            </div>
          </form>

          {/* Travel Mood Pills */}
          <div className="mt-6 max-w-4xl mx-auto">
            <TravelMoodPills variant="dark" />
          </div>

          {/* Stat cards */}
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-2xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4" data-testid="stat-businesses">
              <div className="text-3xl font-bold font-heading">{liveBusinesses}+</div>
              <div className="text-sm text-white/80">Businesses</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4" data-testid="stat-cities">
              <div className="text-3xl font-bold font-heading">{liveCities}</div>
              <div className="text-sm text-white/80">Cities</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4" data-testid="stat-rating">
              <div className="text-3xl font-bold font-heading">{liveRating}</div>
              <div className="text-sm text-white/80">Avg Rating</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
